import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { FromSchema } from 'json-schema-to-ts';

import { getPersistedState } from '../../services/LocalStorageService';
import { RootState } from '../../stores/AppStore';
import { BillingPeriod, TrackGroup } from '../../types';
import { apiSlice } from '../../services/apiSlice';

// Migrate localStorage from old 'numberOfLicenses' to new 'licenseCount'
// This runs once on module load to handle existing users
const migrateLocalStorage = () => {
  try {
    const stored = localStorage.getItem('signUp-state');
    if (stored) {
      const parsed = JSON.parse(stored);
      if (
        parsed.numberOfLicenses !== undefined &&
        parsed.licenseCount === undefined
      ) {
        parsed.licenseCount = parsed.numberOfLicenses;
        delete parsed.numberOfLicenses;
        localStorage.setItem('signUp-state', JSON.stringify(parsed));
      }
    }
  } catch {
    // Ignore parse errors
  }
};
migrateLocalStorage();

const signUpStateSchema = {
  type: 'object',
  properties: {
    step: { type: 'number' },
    stepTitle: { type: 'string' },
    sessionId: { type: 'string' },
    allPrices: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          id: { type: 'string' },
          active: { type: 'boolean' },
          lookup_key: { type: 'string' },
          product: { type: 'string' },
          unit_amount: { type: 'number' },
        },
        required: ['id', 'active', 'lookup_key', 'product', 'unit_amount'],
      },
    },
    licenseCount: { type: 'number' },
    trackGroup: { type: 'string' },
    thinkers: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          firstName: { type: 'string' },
          lastName: { type: 'string' },
          email: { type: 'string' },
          trackId: { type: 'string' },
        },
        required: ['firstName', 'lastName', 'email', 'trackId'],
        additionalProperties: false,
      },
    },
    mentor: {
      type: 'object',
      properties: {
        firstName: { type: 'string' },
        lastName: { type: 'string' },
        email: { type: 'string' },
        phone: { type: 'string' },
        numberOfChildren: { type: 'string' },
        goal: { type: 'string' },
        preferredDevice: { type: 'string' },
        referral: { type: 'string' },
        updateToken: { type: 'string' },
        mentorUserId: { type: 'string' },
        isReturningMentor: { type: 'boolean' },
      },
      required: ['firstName', 'lastName', 'email', 'phone'],
      additionalProperties: false,
    },
    paymentIntentId: { type: 'string' },
    amounts: {
      type: 'object',
      properties: {
        billingPeriod: { type: 'string' },
        amount: { type: 'number' },
        discount: { type: 'number' },
        discountCode: { type: 'string' },
        tax: { type: 'number' },
        total: { type: 'number' },
      },
      required: ['billingPeriod', 'amount'],
      additionalProperties: false,
    },
  },
  required: [
    'allPrices',
    'step',
    'stepTitle',
    'sessionId',
    'licenseCount',
    'thinkers',
    'mentor',
    'amounts',
  ],
  additionalProperties: false,
} as const;

export type SignUpState = FromSchema<typeof signUpStateSchema>;

export const defaultBillingPeriod: BillingPeriod = 'month';

// Clean default state - always used for reset operations
// This ensures resetSignUp clears sessionId even if localStorage had a stale value at module load
const defaultSignUpState: SignUpState = {
  step: 0,
  stepTitle: '',
  sessionId: '',
  allPrices: [],
  licenseCount: 1,
  trackGroup: TrackGroup.gradeLevelMath,
  thinkers: [{ firstName: '', lastName: '', email: '', trackId: '' }],
  mentor: {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    isReturningMentor: false,
  },
  amounts: {
    billingPeriod: defaultBillingPeriod,
    amount: 0,
  },
};

// Hydrated state from localStorage - only used for initial Redux store creation
const initialState =
  getPersistedState<SignUpState>('signUp', signUpStateSchema) ??
  defaultSignUpState;

export const signUpSlice = createSlice({
  name: 'signUp',
  initialState,
  extraReducers: (builder) => {
    builder.addMatcher(
      apiSlice.endpoints.getAllPrices.matchFulfilled,
      (state, action) => {
        state.allPrices = action.payload;
      },
    );
  },
  reducers: {
    setStep: (state, action: PayloadAction<number>) => {
      state.step = action.payload;
      switch (action.payload) {
        case 0: {
          state.stepTitle = '';
          break;
        }
        case 1: {
          state.stepTitle = 'Online Checkout';
          break;
        }
        case 2: {
          state.stepTitle = 'Mentor Details';
          break;
        }
        case 3 + state.licenseCount * 2: {
          state.stepTitle = 'Questionnaire';
          break;
        }
        case 4 + state.licenseCount * 2: {
          state.stepTitle = '';
          break;
        }
        default: {
          if ((action.payload - 3) % 2 === 0) {
            state.stepTitle = `Student ${
              Math.floor((action.payload - 3) / 2) + 1
            } - Personal Details`;
          } else if ((action.payload - 3) % 2 === 1) {
            state.stepTitle = `Student ${
              Math.floor((action.payload - 3) / 2) + 1
            } - Learning Track`;
          }
        }
      }
    },
    setLicenseCount: (state, action: PayloadAction<number>) => {
      state.licenseCount = action.payload;
    },
    setTrackGroup: (state, action: PayloadAction<TrackGroup>) => {
      state.trackGroup = action.payload;
    },
    setThinkers: (state, action: PayloadAction<SignUpState['thinkers']>) => {
      state.thinkers = action.payload;
    },
    setThinkerTrack: (
      state,
      action: PayloadAction<{ index: number; trackId: string }>,
    ) => {
      state.thinkers[action.payload.index].trackId = action.payload.trackId;
    },
    setEmail: (state, action: PayloadAction<string>) => {
      state.mentor.email = action.payload;
    },
    setMentor: (state, action: PayloadAction<SignUpState['mentor']>) => {
      state.mentor = action.payload;
    },
    setUpdateToken: (state, action: PayloadAction<string>) => {
      state.mentor.updateToken = action.payload;
    },
    setMentorUserId: (state, action: PayloadAction<string>) => {
      state.mentor.mentorUserId = action.payload;
    },
    setIsReturningMentor: (state, action: PayloadAction<boolean>) => {
      state.mentor.isReturningMentor = action.payload;
    },
    setPaymentIntentId: (state, action: PayloadAction<string>) => {
      state.paymentIntentId = action.payload;
    },
    setSessionId: (state, action: PayloadAction<string>) => {
      state.sessionId = action.payload;
    },
    setAmounts: (
      state,
      action: PayloadAction<{
        billingPeriod?: BillingPeriod | string;
        amount?: number;
        discount?: number;
        discountCode?: string;
        tax?: number;
        total?: number;
      }>,
    ) => {
      state.amounts = {
        ...state.amounts,
        ...action.payload,
      };
    },
    restoreFromSession: (
      state,
      action: PayloadAction<{
        sessionId: string;
        currentStep: number;
        formData: Record<string, any>;
      }>,
    ) => {
      const { sessionId, currentStep, formData } = action.payload;

      // Restore sessionId and step
      state.sessionId = sessionId;
      state.step = currentStep;

      // Restore form data if available
      if (formData.mentor) {
        state.mentor = { ...state.mentor, ...formData.mentor };
      }
      if (formData.thinkers) {
        state.thinkers = formData.thinkers;
      }
      if (formData.licenseCount) {
        state.licenseCount = formData.licenseCount;
      }
      if (formData.trackGroup) {
        state.trackGroup = formData.trackGroup;
      }
      if (formData.paymentIntentId) {
        state.paymentIntentId = formData.paymentIntentId;
      }
      if (formData.amounts) {
        state.amounts = { ...state.amounts, ...formData.amounts };
      }
    },
    resetSignUp: (state, action: PayloadAction<number>) =>
      action.payload > 0
        ? { ...defaultSignUpState, step: action.payload }
        : defaultSignUpState,
  },
});

export const {
  setStep,
  setLicenseCount,
  setTrackGroup,
  setThinkers,
  setThinkerTrack,
  setEmail,
  setMentor,
  setUpdateToken,
  setMentorUserId,
  setIsReturningMentor,
  setPaymentIntentId,
  setSessionId,
  setAmounts,
  restoreFromSession,
  resetSignUp,
} = signUpSlice.actions;

export const selectStep = (state: RootState) => state.signUp.step;
export const selectStepTitle = (state: RootState) => state.signUp.stepTitle;
export const selectLicenseCount = (state: RootState) =>
  state.signUp.licenseCount;
export const selectTrackGroup = (state: RootState) => state.signUp.trackGroup;
export const selectThinkers = (state: RootState) => state.signUp.thinkers;
export const selectThinkerFirstNames = (state: RootState) =>
  state.signUp.thinkers.map((t) => t.firstName);
export const selectMentor: (state: RootState) => typeof state.signUp.mentor = (
  state: RootState,
) => state.signUp.mentor;
export const selectAmounts = (state: RootState) => state.signUp.amounts;
export const selectPaymentIntentId = (state: RootState) =>
  state.signUp.paymentIntentId;
export const selectSessionId = (state: RootState) => state.signUp.sessionId;
export const selectUpdateToken = (state: RootState) =>
  state.signUp.mentor.updateToken;
export const selectMentorUserId = (state: RootState) =>
  state.signUp.mentor.mentorUserId;
export const selectIsReturningMentor = (state: RootState) =>
  state.signUp.mentor.isReturningMentor;

export default signUpSlice.reducer;
